'use client';

import { VersionedContent } from '@/shared/versionedContent';
import { Button } from '@altus-ui/components_v2/v3/Button';
import React, { useState } from 'react';
import { useGlobalClientContext } from '../common/GlobalClientContext';
import Select from '../components/VersionSelect';
import MarkupRenderer from './MarkupRenderer';
import { extractHeadings } from './utils';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ArrowIcon from './icons/ArrowIcon';

interface PrivacyNoticeProps {
  privacyVersions: VersionedContent[];
}

const PrivacyNotice: React.FC<PrivacyNoticeProps> = ({ privacyVersions }) => {
  const [selectedVersion, setSelectedVersion] = useState<VersionedContent>(
    privacyVersions[0] // Default to latest version
  );

  const {
    i18nHelper: { t },
    language
  } = useGlobalClientContext();

  const headings = selectedVersion.markdownContent
    ? extractHeadings(selectedVersion.markdownContent[language])
    : [];

  return (
    <div className="flex flex-col max-w-[1280px] mx-auto">
      {/* Header (height = 68px) */}
      <Header />
      <div className="flex flex-col mt-[132px] w-full gap-y-9xl">
        <div className="flex flex-col gap-y-7xl pb-9xl border border-l-0 border-r-0 border-t-0 border-b border-gray-100">
          <div className="space-y-2xl">
            <h1 className="font-display font-semibold !text-4xl !leading-18xl">
              {t('privacy.notice.title', {})}
            </h1>
          </div>
          {/* Index */}
          {headings.length > 0 && (
            <div className="flex flex-col gap-y-s">
              {headings
                .filter((heading) => heading.level === 2) // Only show ## headings
                .map((heading, index) => (
                  <Button
                    variant="text"
                    size="M"
                    className="font-button !h-auto !w-full !p-0 !text-cic-blue-500 text-left font-semibold text-md leading-sm [&>span]:!w-full [&>span]:!justify-start"
                    key={index}
                    onClick={() => {
                      const element = document.getElementById(heading.id);
                      if (element) {
                        // Get the element's position
                        const elementPosition =
                          element.getBoundingClientRect().top;
                        // Get the current scroll position
                        const offsetPosition =
                          elementPosition + window.pageYOffset - 120; // 120px offset for header

                        // Scroll to the element with offset
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'auto'
                        });
                      }
                    }}>
                    {heading.text}
                  </Button>
                ))}
            </div>
          )}
        </div>

        {/* Privacy Content */}
        <MarkupRenderer
          content={selectedVersion.markdownContent[language] || ''}
        />

        <div className="flex flex-col gap-4xs !text-cic-blue-500 !text-sm font-semibold leading-sm mb-9xl">
          <div className="flex flex-row gap-3xs">
            <a href="https://www.kopico.go.kr" className="font-button text-cic-blue-500 font-semibold text-sm leading-sm">
              {t('public.site.private.policy.link', {})}
            </a>
            <ArrowIcon />
          </div>
          <div className="flex flex-row gap-3xs">
            <a href="https://privacy.kisa.or.kr" className="font-button text-cic-blue-500 font-semibold text-sm leading-sm">
              {t('public.site.private.policy.link1', {})}
            </a>
            <ArrowIcon />
          </div>
          <div className="flex flex-row gap-3xs">
            <a href="https://www.spo.go.kr" className="font-button text-cic-blue-500 font-semibold text-sm leading-sm">
              {t('public.site.private.policy.link2', {})}
            </a>
            <ArrowIcon />
          </div>
          <div className="flex flex-row gap-3xs">
            <a href="https://ecrm.cyber.go.kr" className="font-button text-cic-blue-500 font-semibold text-sm leading-sm">
              {t('public.site.private.policy.link3', {})}
            </a>
            <ArrowIcon />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3xl mb-10xl">
        <hr className="border-gray-200 m-0 w-full" />

        {/* Date Effective */}
        <p className="font-semibold !text-md leading-xl">
          {t('privacy.effective_date', {
            date:
              language === 'ko-KR'
                ? selectedVersion.lastUpdated
                : new Date(selectedVersion.lastUpdated).toLocaleDateString(
                    'en-US',
                    { year: 'numeric', month: 'long', day: 'numeric' }
                  )
          })}
        </p>

        {/* Version Selector */}
        <div className="w-[320px] h-[40px] border border-gray-300 rounded-md px-s">
          <Select
            options={privacyVersions.map((version) => ({
              value: version.version,
              label:
                language === 'ko-KR'
                  ? version.lastUpdated
                  : new Date(version.lastUpdated).toLocaleDateString(
                      'en-US',
                      { year: 'numeric', month: 'long', day: 'numeric' }
                    )
            }))}
            defaultLabel={t('public.site.button.other_versions', {})}
            defaultValue={privacyVersions[0].version}
            onChange={(value) => {
              if (value === 'otherVersions') {
                return;
              }
              const version = privacyVersions.find((v) => v.version === value);
              if (version) setSelectedVersion(version);
            }}
            dropDownClassName="w-[320px]"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyNotice;
